<template>
  <div>
    <header class="mb-6">
      <h1 class="text-2xl font-bold">Dashboard</h1>
      <p class="text-ink-soft text-sm mt-1">
        EpiBreath clinical overview · {{ user && user.organization }}
      </p>
    </header>

    <div v-if="loading" class="text-ink-soft py-16 text-center">
      <font-awesome-icon icon="circle-notch" spin class="mr-2" /> Loading…
    </div>

    <div v-else>
      <!-- Stat cards -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Measurements</p>
          <p class="text-3xl font-extrabold mt-2">{{ stats.total_measurements }}</p>
        </div>
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Valid</p>
          <p class="text-3xl font-extrabold mt-2 text-green-600">{{ validCount }}</p>
        </div>
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Avg total VPA</p>
          <p class="text-3xl font-extrabold mt-2 text-brand-600">{{ avgVpa }}</p>
        </div>
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Suitability tests</p>
          <p class="text-3xl font-extrabold mt-2">{{ suitabilityTotal }}</p>
        </div>
      </div>

      <div class="grid lg:grid-cols-2 gap-6">
        <!-- Validation breakdown -->
        <div class="card p-6">
          <h2 class="font-bold mb-4">Validation status</h2>
          <div v-for="(count, label) in stats.by_validation_status" :key="label" class="mb-3">
            <div class="flex justify-between text-sm mb-1">
              <span class="capitalize text-ink-soft">{{ label }}</span>
              <span class="font-semibold">{{ count }}</span>
            </div>
            <div class="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="barColor(label)"
                :style="{ width: pct(count) + '%' }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Recent measurements -->
        <div class="card p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="font-bold">Recent measurements</h2>
            <router-link :to="{ name: 'Measurements' }" class="text-sm font-semibold text-brand-600">
              View all →
            </router-link>
          </div>
          <ul class="divide-y divide-gray-50">
            <li v-for="m in recent" :key="m.uuid" class="py-2.5 flex items-center justify-between">
              <router-link
                :to="{ name: 'MeasurementDetail', params: { uuid: m.uuid } }"
                class="min-w-0"
              >
                <p class="text-sm font-semibold text-ink truncate">
                  {{ m.patient.first_name }} {{ m.patient.last_name }}
                </p>
                <p class="text-xs text-ink-muted">{{ fmtDate(m.measurement_timestamp) }}</p>
              </router-link>
              <span class="pill" :class="statusPill(m.validation_status)">
                {{ m.validation_status || 'n/a' }}
              </span>
            </li>
            <li v-if="!recent.length" class="py-6 text-center text-ink-muted text-sm">
              No measurements.
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getterTypes } from 'store/auth'
import { api } from 'helpers/api'
import { fmtDate, statusPill } from 'helpers/format'

export default {
  data() {
    return {
      loading: true,
      stats: { total_measurements: 0, by_validation_status: {}, avg_total_vpa: null },
      recent: [],
      suitabilityTotal: 0,
    }
  },
  computed: {
    ...mapGetters({ user: getterTypes.currentUser }),
    validCount() {
      return this.stats.by_validation_status.valid || 0
    },
    avgVpa() {
      return this.stats.avg_total_vpa == null ? '—' : this.stats.avg_total_vpa.toFixed(1)
    },
    maxBar() {
      const vals = Object.values(this.stats.by_validation_status)
      return vals.length ? Math.max(...vals) : 1
    },
  },
  async created() {
    try {
      const [stats, recent, suit] = await Promise.all([
        api.getStats(),
        api.listMeasurements({ page: 1, perPage: 6 }),
        api.listSuitability({ page: 1, perPage: 1 }),
      ])
      this.stats = stats
      this.recent = recent.data
      this.suitabilityTotal = suit.total
    } finally {
      this.loading = false
    }
  },
  methods: {
    fmtDate,
    statusPill,
    pct(count) {
      return Math.round((count / this.maxBar) * 100)
    },
    barColor(label) {
      return (
        { valid: 'bg-green-500', invalid: 'bg-red-500', error: 'bg-amber-500' }[label] ||
        'bg-gray-400'
      )
    },
  },
}
</script>
